import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { useUserData } from "../../auth/useUserData.js";
import { BreadcrumbItem, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import { ManagePropertiesTable } from "../../features/item-properties/ManagePropertiesTable.js";
import { QuoteAnnotationsBanner } from "../../features/quote-annotations/QuoteAnnotationsBanner.js";
import { QuoteSubHeader } from "../../features/quotes/QuoteSubHeader.js";
import { useHasSapS4HanaIntegration } from "../../features/sap-s4-hana/hooks.js";
import { useProjectQuotePropertiesQuery } from "./ProjectQuoteProperties.generated.js";

interface Props {
  projectId: string;
  quoteId: string;
  submenuItems: PageTopbarItem[];
  prefixBreadcrumbs: BreadcrumbItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
  pathToDocList: string;
  pathToDoc: string;
}

const REFETCH_QUERIES = [namedOperations.Query.ProjectQuoteProperties];

export function ProjectQuoteProperties({
  projectId,
  quoteId,
  submenuItems,
  prefixBreadcrumbs,
  activeSubmenuItem,
  pathToDocList,
  pathToDoc,
}: Props) {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  const pathToDocPage = `${pathToDoc}/properties`;

  const viewer = useUserData().currentUser!;

  const client = useApolloClient();
  const query = useProjectQuotePropertiesQuery({
    client,
    variables: { docId: quoteId, projectId },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });
  const doc = getDataOrNull(query.data?.quote)?.quote;
  const project = getDataOrNull(query.data?.project)?.project;

  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();

  const isAgreed =
    (doc?.isPublished && doc?.isBinding && doc?.agreement === "YES") ?? false;
  const isViewersQuote =
    doc?.owningSystemOrganisationId === viewer.organisation.id;

  return !query.loading && doc ? (
    <ManagePropertiesTable
      docType="QUOTE"
      documentViewerPermissions={doc.viewerPermissions}
      items={doc.items}
      projectId={projectId}
      docId={quoteId}
      topContent={<QuoteAnnotationsBanner sx={{ mb: 2 }} />}
      pageProps={{
        title: doc?.title ?? "",
        subtitle: t("Manage properties", { ns: "QuoteItem" }),
        submenuItems: submenuItems,
        breadcrumbs: prefixBreadcrumbs,
        subHeader:
          project && doc ? (
            <QuoteSubHeader
              project={project}
              quote={doc}
              activeView="properties"
              canEdit={isViewersQuote}
              isReadOnly={isAgreed}
              pathToDocList={pathToDocList}
              pathToDoc={pathToDoc}
              hasSapS4HanaIntegration={hasSapS4HanaIntegration}
              isHeaderVisible={undefined as never}
              setHeaderVisible={undefined as never}
              expandedItemIds={undefined}
              onUpdateDataRefetchQueries={REFETCH_QUERIES}
            />
          ) : undefined,
        header: (
          <PageHeader
            breadcrumbs={prefixBreadcrumbs}
            submenuItems={submenuItems}
            activeSubmenuItem={activeSubmenuItem}
          />
        ),
      }}
    />
  ) : null;
}
